import type { Permissions, RolesWithPermissions, User } from '../types';
import { SHARED_CLIENT_POOL_BUSINESS_UNIT_IDS } from './constants';

const hasBusinessUnitAccess = (user: User, targetUser: Omit<User, 'id'> & { id?: number }): boolean => {
  return !!user.businessUnitIds?.some((businessUnitId) => targetUser.businessUnitIds?.includes(businessUnitId));
};

const branchOfficeMatches = (user: User, targetUser: Omit<User, 'id'> & { id?: number }): boolean => {
  if (!user.branchOffice || user.branchOffice.trim() === '') return true;
  if (!targetUser.branchOffice || targetUser.branchOffice.trim() === '') return true;
  return targetUser.branchOffice === user.branchOffice;
};

const brandsToSellOverlap = (user: User, targetUser: Omit<User, 'id'> & { id?: number }): boolean => {
  if (!user.brandsToSell || user.brandsToSell.length === 0) return true;
  return user.brandsToSell.some((brand) => targetUser.brandsToSell?.includes(brand)) ?? false;
};

const branchManagerCanManageSeller = (user: User, targetUser: Omit<User, 'id'> & { id?: number }): boolean => {
  if (!targetUser) return false;
  if (!hasBusinessUnitAccess(user, targetUser)) return false;
  if (targetUser.isAdmin === true) return false;
  if (targetUser.role !== 'seller' && targetUser.role !== 'sellerWithAssignment') return false;

  if (user.assignedSellerIds && user.assignedSellerIds.length > 0) {
    return targetUser.id != null && user.assignedSellerIds.includes(targetUser.id);
  }
  return brandsToSellOverlap(user, targetUser) && branchOfficeMatches(user, targetUser);
};

const ROLES = {
  admin: {
    clients: {
      viewAndUpdate: true,
      assign: true,
    },
    users: {
      view: true,
      update: true,
      create: true,
      delete: true,
      assignSellers: true,
    },
  },
  businessUnitAdmin: {
    clients: {
      viewAndUpdate: (user, client) => user.businessUnitIds?.includes(client.businessUnitId) || false,
      assign: (user, client) => (client && user.businessUnitIds?.includes(client.businessUnitId)) || false,
    },
    users: {
      view: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.role !== 'admin' &&
        targetUser.isAdmin !== true,
      update: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.role !== 'admin' &&
        targetUser.isAdmin !== true,
      create: (user, targetUser) => {
        if (!targetUser) return true; // This means that the user is being created and doesn't have businessUnitIds yet
        return hasBusinessUnitAccess(user, targetUser) && targetUser.role !== 'admin' && targetUser.isAdmin !== true;
      },
      delete: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.role !== 'admin' &&
        targetUser.isAdmin !== true,
      assignSellers: true,
    },
  },
  branchManager: {
    clients: {
      viewAndUpdate: (user, client) => {
        if (
          !user.businessUnitIds?.includes(client.businessUnitId) ||
          client.isTestClient ||
          !['whatsapp', 'instagram'].includes(client.chatType || '')
        )
          return false;
        // Assigned sellers mode: only own + assigned sellers' clients
        if (user.assignedSellerIds && user.assignedSellerIds.length > 0) {
          return client.userId === user.id || (client.userId != null && user.assignedSellerIds.includes(client.userId));
        }
        // Default mode: all clients in BU filtered by brand
        return user.brandsToSell && user.brandsToSell.length > 0
          ? user.brandsToSell.includes(client.brandOfInterest || '')
          : true;
      },
      assign: (user, client) =>
        (client &&
          user.businessUnitIds?.includes(client.businessUnitId) &&
          (user.brandsToSell && user.brandsToSell.length > 0
            ? user.brandsToSell.includes(client.brandOfInterest || '')
            : true)) ||
        false,
    },
    users: {
      view: (user, targetUser) => !!targetUser && branchManagerCanManageSeller(user, targetUser),
      update: (user, targetUser) => !!targetUser && branchManagerCanManageSeller(user, targetUser),
      create: (user, targetUser) => {
        if (!targetUser) return true; // This means that the user is being created and doesn't have businessUnitIds yet
        return branchManagerCanManageSeller(user, targetUser);
      },
      delete: (user, targetUser) => !!targetUser && branchManagerCanManageSeller(user, targetUser),
    },
  },
  seller: {
    clients: {
      viewAndUpdate: (user, client) =>
        (user.businessUnitIds?.includes(client.businessUnitId) || false) &&
        !client.isTestClient &&
        (client.userId === user.id || SHARED_CLIENT_POOL_BUSINESS_UNIT_IDS.includes(client.businessUnitId)),
      assign: false,
    },
    users: {
      view: (user, targetUser) => !!targetUser && targetUser.id === user.id,
      update: (user, targetUser) => !!targetUser && targetUser.id === user.id,
    },
  },
  sellerWithAssignment: {
    clients: {
      viewAndUpdate: (user, client) =>
        (user.businessUnitIds?.includes(client.businessUnitId) || false) &&
        !client.isTestClient &&
        (client.userId === user.id || SHARED_CLIENT_POOL_BUSINESS_UNIT_IDS.includes(client.businessUnitId)),
      assign: (user, client) => (client && user.businessUnitIds?.includes(client.businessUnitId)) || false,
    },
    users: {
      view: (user, targetUser) => !!targetUser && targetUser.id === user.id,
      update: (user, targetUser) => !!targetUser && targetUser.id === user.id,
    },
  },
} as const satisfies RolesWithPermissions;

export function hasPermission<Resource extends keyof Permissions>(
  user: User,
  resource: Resource,
  action: Permissions[Resource]['action'],
  data?: Permissions[Resource]['dataType']
): boolean {
  const permission = (ROLES as RolesWithPermissions)[user.role][resource]?.[action];
  if (permission === null || permission === undefined) return false;
  if (typeof permission === 'boolean') return permission;
  if (data === undefined) return false;
  return permission(user, data);
}
